﻿import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { CrudFunctions } from '../utilities/crud-functions';
import { ProductStandard } from '../models/product-standard';
import { AnalyticalPlanStandard } from '../models/analytical-plan-standard';
import StandardsSearch from './StandardsSearch';
import AddStandardDialog from './Standards/AddStandardDialog';
import { StandardReference } from '../models/Standards/standard-reference';
import { Entities } from '../models/enums';
import { ItemBasicInformation } from '../models/item-basic-information';
import AuditHistoryComponent from './AuditHistoryComponent';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import GlobalDialog from './Modal/GlobalDialog';
import { StandardsUtil } from '../utilities/standards-util';

import DataGrid, {
    Column,
    Paging,
    Toolbar,
    Selection,
    Editing,
    Popup,
    Form,
    Lookup,
    Button as ColumnButton,
    Item as ToolbarItem
} from 'devextreme-react/data-grid';

import { Popup as DialogPopup } from 'devextreme-react/popup';
import Button from 'devextreme-react/button';
import { Item } from 'devextreme-react/form';

let key = 0;

const productStandardTypes = [
    { id: 1, name: 'Qualitative' },
    { id: 2, name: 'Quantitative' }
]

export interface Props {
    productId: string;
    isReadOnly: boolean;
    title?: string;
    onProductStandardsUpdated?: () => void;
}

export default function ProductStandardsData(props: Props) {
    const [productStandards, setProductStandards] =
        useState<ProductStandard[]>();

    const [selectedProductStandard, setSelectedProductStandard] =
        useState<ProductStandard>();

    const [analyticalPlanStandards, setAnalyticalPlanStandards] =
        useState<AnalyticalPlanStandard[]>();

    const [selectedAnalyticalPlanStandard, setSelectedAnalyticalPlanStandard] =
        useState<AnalyticalPlanStandard>();

    const [standardsSearchDialogVisible, setStandardsSearchDialogVisible] =
        useState<boolean>(false);

    const [addStandardDialogVisible, setAddStandardDialogVisible] =
        useState<boolean>(false);

    const [auditRowPointer, setAuditRowPointer] =
        useState<string>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [auditInformationDialogVisible, setAuditInformationDialogVisible] =
        useState<boolean>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>()

    useEffect(() => {
        getAllData()
    }, [props.productId]);

    key++;

    return (
        <div>
            <div>
                <DataGrid height="16rem"
                    dataSource={productStandards ? productStandards : []}
                    showBorders={true}
                    allowColumnResizing={true}
                    wordWrapEnabled={true}
                    onSelectionChanged={onProductStandardSelectionChanged}
                    onRowValidating={onProductStandardValidating}
                    onRowRemoving={onProductStandardRemoving}
                    columnResizingMode="nextColumn"

                    rowAlternationEnabled={true}
                    showRowLines={true} >

                    <Paging enabled={false} />
                    <Selection mode='single' />

                    <Column type="buttons" width='7rem' >
                        <ColumnButton hint="audit information" icon="clock"
                            onClick={onStandardAuditButtonClicked} />
                        <ColumnButton hint="information" icon="info"
                            onClick={onStandardInformationButtonClicked} />
                        <ColumnButton hint="edit standard" name="edit" />
                        <ColumnButton hint="remove standard" name="delete" />
                    </Column>

                    <Column dataField='manufacturerName'
                        width='8rem' caption="Manufacturer" />

                    <Column dataField='productName' />

                    <Column dataField='catalogNumber' width='7rem' caption="Catalog #" />

                    <Column dataField="catalogReference" caption="" alignment="center"
                        width='2rem' cssClass="table-vertical-align"
                        cellRender={catalogReferenceCellRender} />

                    <Column dataField='netWeightString' caption="Net Weight" width='7rem' dataType="string" />

                    <Column dataField='standardTypeId' caption="Type" dataType="number" width="7rem" >
                        <Lookup dataSource={productStandardTypes ? productStandardTypes : []}
                            valueExpr='id' displayExpr='name' />
                    </Column>

                    <Column dataField='unitsNeeded' dataType="number" width='5rem' cellRender={unitsNeededCellRender} />
                    <Column dataField='totalAllocated' dataType="number" width='8rem' />

                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Product Standards</div>
                            </div>
                        </ToolbarItem>

                        {!props.isReadOnly &&
                            <ToolbarItem location="after">
                                <Button id="addProductStandardButton" icon="plus" type="success"
                                    className="pw-button" hint="add product standard"
                                    height='1.8rem'
                                    onClick={onAddProductStandardClick} />
                            </ToolbarItem>
                        }

                        {!props.isReadOnly &&
                            <ToolbarItem location="after">
                                <Button id="searchButton" text="Search" icon="search" type="success"
                                    className="pw-button" hint="search"
                                    height='1.8rem'
                                    onClick={onShowStandardsSearchClick} />
                            </ToolbarItem>
                        }
                    </Toolbar>

                    <Editing
                        mode="popup"
                        useIcons={true}
                        allowUpdating={!props.isReadOnly}
                        allowDeleting={!props.isReadOnly}>

                        <Popup title="Product Standard" showTitle={true} width="25rem" height="12rem" />
                        <Form>
                            <Item dataField="standardTypeId" colSpan={2} />
                            <Item dataField="unitsNeeded" />
                        </Form>

                    </Editing>
                </DataGrid>

            </div>

            <div className="pw-margin-top-1">
                <DataGrid height="16rem"
                    dataSource={analyticalPlanStandards ? analyticalPlanStandards : []}
                    showBorders={true}
                    allowColumnResizing={true}
                    wordWrapEnabled={true}
                    onSelectionChanged={onAnalyticalPlanStandardSelectionChanged}
                    onRowValidating={onAnalyticalPlanStandardValidating}
                    onRowRemoving={onAnalyticalPlanStandardRemoving}
                    columnResizingMode="nextColumn"
                    rowAlternationEnabled={true}
                    showRowLines={true} >

                    <Paging enabled={false} />
                    <Selection mode='single' />

                    <Column type="buttons" width='7rem' >
                        <ColumnButton hint="audit information" icon="clock"
                            onClick={onStandardAuditButtonClicked} />
                        <ColumnButton hint="information" icon="info"
                            onClick={onStandardInformationButtonClicked} />
                        <ColumnButton hint="edit standard" name="edit" />
                        <ColumnButton hint="remove standard" name="delete" />
                    </Column>

                    <Column dataField='technologyName' width='8rem' caption="Technology" />
                    <Column dataField="scopeTypesString" caption="Scopes" />
                    <Column dataField='standardId' dataType="string" visible={false} >
                        <Lookup dataSource={productStandards ? productStandards : []}
                            valueExpr='standardId' displayExpr={technologyStandardDropdownItemRender} />
                    </Column>

                    <Column dataField='manufacturerName' caption="Manufacturer" width='10rem'
                        cellRender={selectedStandardManufacturerCellRender} />

                    <Column dataField='catalogNumber' caption="Catalog #" width='8rem' dataType="string"
                        cellRender={selectedStandardCatalogNumberCellRender} />

                    <Column dataField='productName' dataType="string" width="10rem" />
                    <Column dataField='netWeightString' caption="Net Weight" dataType="string" width='7rem' />
                    <Column dataField='unitsNeeded' dataType="number" width='8rem' />
                    <Column dataField='totalVolumeString' caption="Total" dataType="string" width='8rem' />
                    
                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Analytical Plan Technology Standards</div>
                            </div>
                        </ToolbarItem>
                    </Toolbar>

                    <Editing
                        mode="popup"
                        useIcons={true}
                        allowUpdating={!props.isReadOnly}
                        allowDeleting={!props.isReadOnly}>

                        <Popup title="Analytical Plan Technology Standard" showTitle={true} width="40rem" height="20rem" />
                        <Form>
                            <Item dataField="standardId" colSpan={2} />
                            <Item dataField="unitsNeeded" />
                        </Form>

                    </Editing>
                </DataGrid>

            </div>

            {addStandardDialogVisible &&
                <DialogPopup
                    visible={addStandardDialogVisible}
                    onHiding={hideAddStandardDialog}
                    height="24rem" width="35rem"
                    dragEnabled={false}
                    hideOnOutsideClick={false}
                    title="Add Standard">
                    <AddStandardDialog productId={props.productId}
                        onCancel={hideAddStandardDialog} />
                </DialogPopup>
            }

            {standardsSearchDialogVisible &&
                <DialogPopup
                    visible={standardsSearchDialogVisible}
                    onHiding={hideStandardsSearchDialog}
                    dragEnabled={true}
                    fullScreen={true}
                    hideOnOutsideClick={false}
                    title="Search Standards">
                    <StandardsSearch onImportSelection={onImportStandardsSearchSelection}
                        onCancel={onStandardsComponentClose} />
                </DialogPopup>
            }

            {auditInformationDialogVisible &&
                <DialogPopup visible={auditInformationDialogVisible}
                    onHiding={hideAuditInformationDialog}
                    title="Audit History"
                    minWidth={'50em'}
                    minHeight={'32em'}
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <AuditHistoryComponent entityNumber={Entities.CompStandards}
                        key={`AuditHistory${key}`}
                        childData={false}
                        rowPointer={auditRowPointer ? auditRowPointer : undefined} />
                </DialogPopup>
            }

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function onAddProductStandardClick() {
        showAddStandardDialog();
    }

    function onShowStandardsSearchClick() {
        showStandardsSearchDialog();
    }

    function onProductStandardSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0)
            setSelectedProductStandard(e.selectedRowsData[0]);
    }

    function onAnalyticalPlanStandardSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0)
            setSelectedAnalyticalPlanStandard(e.selectedRowsData[0]);
    }

    async function onProductStandardRemoving(e: any) {
        if (e.data && e.data.productId && e.data.standardId) {
            await deleteProductStandard(e.data.productId, e.data.standardId)
                .then(() => {
                    getProductStandardsData();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Deleting Product Standard");
                });
        }
    }

    async function onAnalyticalPlanStandardRemoving(e: any) {
        if (e.data && e.data.analyticalPlanId && e.data.standardId) {
            await deleteAnalyticalPlanStandard(e.data.analyticalPlanId, e.data.standardId)
                .then(() => {
                    getAnalyticalPlansStandardsData();
                    if (props.onProductStandardsUpdated)
                        props.onProductStandardsUpdated();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Deleting Analytical Plan Standard");
                });
        }
    }

    async function onProductStandardValidating(e: any) {
        if (e.oldData) {
            let standard = e.oldData as ProductStandard;

            if (standard) {
                let updatedStandard = { ...standard, ...e.newData };

                await updateProductStandard(updatedStandard)
                    .then(() => {
                        getProductStandardsData();
                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Product Standard");
                    });
            }
        }
    }

    async function onAnalyticalPlanStandardValidating(e: any) {
        if (e.oldData) {
            let standard = e.oldData as AnalyticalPlanStandard;

            if (standard) {
                let updatedStandard = { ...standard, ...e.newData };

                await updateAnalyticalPlanStandard(updatedStandard)
                    .then(() => {
                        getAnalyticalPlansStandardsData();

                        getAnalyticalPlansStandardsAllocationsTotals(standard.standardId);

                        if (props.onProductStandardsUpdated)
                            props.onProductStandardsUpdated();
                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Analytical Plan Technology Standard");
                    });
            }
        }
    }

    async function onImportStandardsSearchSelection(item: StandardReference) {
        if (item.standardId) {
            const newProductStandard: ProductStandard = {
                productId: props.productId,
                standardId: item.standardId
            };

            await insertProductStandard(newProductStandard)
                .then(data => {
                    getProductStandardsData();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Inserting Standard");
                });
        }

        hideStandardsSearchDialog();
    }

    function onStandardsComponentClose() {
        hideStandardsSearchDialog();
    }

    async function getProductStandardsData() {
        await CrudFunctions.getData(`products/standards/${props.productId}`)
            .then((data) => {
                setProductStandards(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Product Standards");
            });
    }

    async function getAnalyticalPlansStandardsData() {
        await CrudFunctions.getData(`products/analyticalplanstandards/${props.productId}`)
            .then((data) => {
                setAnalyticalPlanStandards(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Analytical Plan Standards");
            });
    }

    async function getAnalyticalPlansStandardsAllocationsTotals(standardId: string) {
        await CrudFunctions.getData(`products/analyticalplanstandardsallocationtotals/${standardId}`)
            .then((data) => {
                if (data && data.value !== null && data.value !== undefined) {
                    let editedProductStandards = [...productStandards];

                    let matchingStandard = editedProductStandards.find(s => s.standardId === standardId);

                    if (matchingStandard)
                        matchingStandard.totalAllocated = data.value;

                    setProductStandards(editedProductStandards);
                }
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Analytical Plan Standards Allocation Totals");
            });
    }

    async function insertProductStandard(standard: ProductStandard) {
        return await CrudFunctions.postData("products/productstandard", standard);
    }

    async function insertAnalyticalPlanStandard(standard: AnalyticalPlanStandard) {
        return await CrudFunctions.postData("products/analyticalplanstandard", standard);
    }

    async function updateProductStandard(standard: ProductStandard) {
        return await CrudFunctions.putData("products/productstandard", standard);
    }

    async function updateAnalyticalPlanStandard(standard: AnalyticalPlanStandard) {
        return await CrudFunctions.putData("products/analyticalplanstandard", standard);
    }

    async function deleteProductStandard(productId: string, standardId: string) {
        return await CrudFunctions.deleteData(`products/productstandard/${productId}/${standardId}`);
    }

    async function deleteAnalyticalPlanStandard(analyticalPlanId: string, standardId: string) {
        return await CrudFunctions.deleteData(`products/analyticalplanstandard/${analyticalPlanId}/${standardId}`);
    }

    async function getAllData() {
        await Promise.all([
            getProductStandardsData(),
            getAnalyticalPlansStandardsData()
        ]);
    }

    //render

    function catalogReferenceCellRender(e: any) {
        if (e.data) {
            let standard = e.data as ProductStandard;

            if (standard && standard.manufacturerId && standard.catalogNumber && standard.currentLotNumber) {

                const path = StandardsUtil.standardCoaUrl(standard.manufacturerId, standard.catalogNumber, standard.currentLotNumber);

                if (path) {
                    return <NavLink to={path} className="datagrid-icon-link"
                        target="_blank" rel="noopener noreferrer">
                        <div className="dx-icon-globe datagrid-icon"></div>
                    </NavLink>;
                }
            }
        }

        return <div></div>;
    }

    function selectedStandardCatalogNumberCellRender(e: any) {
        let css = "";

        if (e.data && e.data.standardId && selectedProductStandard &&
            e.data.standardId === selectedProductStandard.standardId) {
            css = "pw-green";
        }

        return (
            <div className={css}>{e.data.catalogNumber}</div>
        );
    }

    function selectedStandardManufacturerCellRender(e: any) {
        let css = "";

        if (e.data && e.data.standardId && selectedProductStandard &&
            e.data.standardId === selectedProductStandard.standardId) {
            css = "pw-green";
        }

        return (
            <div className={css}>{e.data.manufacturerName}</div>
        );
    }

    function unitsNeededCellRender(e: any) {
        let css = "pw-green";

        if (e.data.totalAllocated > e.data.unitsNeeded)
            css = "pw-red";

        return (
            <div className={css}>{e.data.unitsNeeded}</div>
        );
    }

    //leave for now:
    function catalogNumberCellRender(e: any) {
        if (e.data.standardId) {
            const url = `/standardsmaintenance/${e.data.standardId}`;

            return (
                <div><a href={url} rel="noopener noreferrer" target="_blank">{e.data.catalogNumber}</a></div>
            );
        }
        else
            return (
                <div>{e.data.catalogNumber}</div>
            );
    }

    function technologyStandardDropdownItemRender(item: ProductStandard) {
        if (item)
            return `${item.manufacturerName} - ${item.catalogNumber}`;
        else
            return "NA";
    }

    function onStandardAuditButtonClicked(e: any) {
        if (e.row && e.row.data) {
            setAuditRowPointer(e.row.data.productStandardId);
            setAuditInformationDialogVisible(true);
        }
    }

    function onStandardInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
            let itemInformation: ItemBasicInformation = {
                rowPointer: e.row.data.productStandardId,
                createTimestamp: e.row.data.createTimestamp,
                lastModifiedTimestamp: e.row.data.modifyTimestamp,
                createUser: e.row.data.createUser,
                modifyUser: e.row.data.modifyUser
            };

            setItemBasicInformation(itemInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function showAddStandardDialog() {
        setAddStandardDialogVisible(true);
    }

    function hideAddStandardDialog() {
        setAddStandardDialogVisible(false);
    }

    function hideAuditInformationDialog() {
        setAuditInformationDialogVisible(false);
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showStandardsSearchDialog() {
        setStandardsSearchDialogVisible(true);
    }

    function hideStandardsSearchDialog() {
        setStandardsSearchDialogVisible(false);
    }
}